import {LANG, YANDEX_LANG} from "./constants";

const DefaultMapState = {
	center: [59.94, 30.30],
	zoom: 13,
	controls: [],
};

const DefaultMapOptions = {
	suppressMapOpenBlock: true,
	yandexMapDisablePoiInteractivity: true,
};

const ControlId = {
	zoomControl: `zoomControl`,
	fullscreenControl: `fullscreenControl`,
};

class YandexMapHelper {
	constructor( ymapsObject, mapProps ) {
		const {
			mapContainer,
			ymapsApiLang,
			mapState: customMapState = {},
			mapOptions: customMapOptions = {},
			component,
		} = mapProps;
		
		const mapState = Object.assign( {}, DefaultMapState, customMapState );
		const mapOptions = Object.assign( {}, DefaultMapOptions, customMapOptions );
		
		this._ymaps = ymapsObject;
		
		this._mapContainer = mapContainer;
		this._component = component;
		
		this._yandexMap = new this._ymaps.Map( mapContainer, mapState, mapOptions );
		
		this._initZoom = this._yandexMap.getZoom();
		this._initCenter = this._yandexMap.getCenter();
		
		this._objectManagers = {};
		
		this._lang = LANG.RU;
		
		if ( ymapsApiLang === YANDEX_LANG.EN || ymapsApiLang === YANDEX_LANG.EN_RU )
		{
			this._lang = LANG.EN;
		}
	}
	
	get mapContainer() {
		return this._mapContainer;
	}

	get component() {
		return this._component;
	}
	
	get yandexMap() {
		return this._yandexMap;
	}
	
	get ymaps() {
		return this._ymaps;
	}
	
	get ControlId() {
		return ControlId;
	}
	
	get initCenter() {
		return this._initCenter;
	}
	
	set initCenter( coords ) {
		this._initCenter = coords;
	}
	
	get initZoom() {
		return this._initZoom;
	}
	
	set initZoom( zoom ) {
		this._initZoom = zoom;
	}
	
	get lang() {
		return this._lang;
	}
	
	addZoomControls( options = {} ) {
		const defaultPosition = {
			top: 0,
			right: 0,
			bottom: `auto`,
			left: `auto`,
		};
		
		const {
			size = `small`,
			position = defaultPosition,
		} = options;
		
		this._yandexMap.controls.add( ControlId.zoomControl, {size, position} );
	}
	
	addFullscreenControls( options = {} ) {
		const defaultPosition = {
			top: 0,
			left: 0,
			bottom: `auto`,
			right: `auto`,
		};
		
		const {
			size = `small`,
			position = defaultPosition,
		} = options;
		
		this._yandexMap.controls.add( ControlId.fullscreenControl, {size, position} );
	}
	
	isTouch() {
		return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test( navigator.userAgent );
	}
	
	setCorrectScrolling( props = {} ) {
		let {
			textScroll,
			textTouch,
		} = props;
		
		if ( !textScroll )
		{
			if ( this._lang === LANG.EN )
			{
				textScroll = `Scroll the map while holding ctrl-key to change zoom`;
			}
			else
			{
				textScroll = `Чтобы изменить масштаб, прокручивайте карту, удерживая клавишу Ctrl`;
			}
		}
		
		if ( !textTouch )
		{
			if ( this._lang === LANG.EN )
			{
				textTouch = `Swipe with two fingers to move the map`;
			}
			else
			{
				textTouch = `Чтобы переместить карту проведите по ней двумя пальцами`;
			}
		}
		
		const isTouch = this.isTouch();
		
		const eventsPane = this._yandexMap.panes.get( `events` );
		const eventsPaneEl = eventsPane.getElement();
		
		const text = isTouch ? textTouch : textScroll;
		
		const styles = {
			alignItems: `center`,
			boxSizing: `border-box`,
			color: `#fff`,
			display: `flex`,
			justifyContent: `center`,
			padding: `40px`,
			textAlign: `center`,
			transition: `background .2s`,
			touchAction: `auto`,
			fontSize: `18px`,
		};
	
		Object.keys( styles ).forEach(
			( name ) => {
				eventsPaneEl.style[name] = styles[name];
			}
		);
	
		const toogleHint = ( isToggleVisible ) => {
			eventsPaneEl.style.background = `rgba(0, 0, 0, ${isToggleVisible ? `.6` : `0`})`;
			eventsPaneEl.textContent = isToggleVisible ? text : ``;
		};
		
		if ( isTouch )
		{
			this._yandexMap.behaviors.disable( `drag` );
			
			this._ymaps.domEvent.manager.add(
				eventsPaneEl,
				`touchmove`,
				( evt ) => toogleHint( evt.get( `touches` ).length === 1 )
			);
			
			ymaps.domEvent.manager.add(
				eventsPaneEl,
				`touchend`,
				() => toogleHint( false )
			);
		}
		else
		{
			const toggleScroll = ( isToggleVisible ) => {
				const behavior = `scrollZoom`;
				
				isToggleVisible
					? this._yandexMap.behaviors.enable( behavior )
					: this._yandexMap.behaviors.disable( behavior );
			};
			
			let isMouseEnter = false;
			let isCtrlPress = false;
			
			toggleScroll( false );
			
			eventsPane.events.add(
				`wheel`,
				() => {
					if ( !isMouseEnter )
					{
						return;
					}
					
					toggleScroll( isCtrlPress );
					toogleHint( !isCtrlPress );
				}
			);
			
			eventsPane.events.add(`mousedown`, () => toogleHint( false ) );
			
			eventsPane.events.add(`mouseenter`, () => isMouseEnter = true );
			
			eventsPane.events.add(`mouseleave`, () => {
				isMouseEnter = false;
				
				toogleHint(false);
			});
			
			document.addEventListener(
				`keydown`,
				( evt ) => {
					isCtrlPress = evt.ctrlKey;
					
					if ( isCtrlPress ) {
						toogleHint(false);
					}
				}
			);
			
			document.addEventListener( `keyup`, () => isCtrlPress = false );
		}
	}
	
	createObjectManager( objectManagerId, options = {} ) {
		this._objectManagers[objectManagerId] = new this._ymaps.ObjectManager( options );
		this._yandexMap.geoObjects.add( this._objectManagers[objectManagerId] );
		
		return this._objectManagers[objectManagerId];
	}
	
	getObjectManager( objectManagerId )
	{
		return this._objectManagers[objectManagerId];
	}
	
	closeBalloon() {
		if ( this.yandexMap.balloon.isOpen() )
		{
			this.yandexMap.balloon.close();
		}
	}

	createNewLayer( layerName, options = {} ) {
		return new this._ymaps.Layer(layerName, options);
	}
}

export {
	YandexMapHelper,
};
